import { ApolloClient, NormalizedCacheObject, useMutation } from '@apollo/client';
import {
  DeleteLessonResultData,
  DeleteLessonResultVars,
  FinishLessonSessionData,
  FinishLessonSessionVars,
  SubmitLessonData,
  SubmitLessonVars,
  UnsubmitLessonData,
  UnsubmitLessonVars,
  UpsertLessonResultData,
  UpsertLessonResultVars
} from '../gql-types';

import ApolloClientService from '../../shared/services/ApolloClientService';
import AuthService from '../../shared/services/AuthService';
import Config from '../../shared/config';
import GraphQL from '../gql';
import { LessonType } from 'dashboard/models/LessonVm';

let dashboardApolloClient: ApolloClient<NormalizedCacheObject> = null;
let assessmentApolloClient: ApolloClient<NormalizedCacheObject> = null;

export function useLessonResults(coursekey?: number) {
  //* onCourseUpdate is optional because the Video component saves the video progress data in JSON as an answer
  //* but it doesn't affect the course progress.
  if (!dashboardApolloClient) {
    dashboardApolloClient = ApolloClientService.create(Config.DASHBOARD_GQL);
  }
  if (!assessmentApolloClient) {
    assessmentApolloClient = ApolloClientService.create(Config.ASSESSMENT_GQL);
  }
  const userkey = AuthService.Userkey;

  const [_upsertLessonResult, { loading, error, data: resultData }] =
    useMutation<UpsertLessonResultData, UpsertLessonResultVars>(
      GraphQL.Mutation.UPSERT_LESSON_RESULT,
      {
        client: dashboardApolloClient
      }
    );

  const [
    _finishLessonSession,
    {
      loading: finishingSession,
      error: finishSessionError,
      data: finishedSessionData
    }
  ] = useMutation<FinishLessonSessionData, FinishLessonSessionVars>(
    GraphQL.Mutation.FINISH_LESSON_SESSION,
    {
      client: dashboardApolloClient
    }
  );

  const [
    _deleteLessonResult,
    { loading: deleting, error: _deleteError, data: deleteData }
  ] = useMutation<DeleteLessonResultData, DeleteLessonResultVars>(
    GraphQL.Mutation.DELETE_LESSON_RESULT,
    {
      client: dashboardApolloClient
    }
  );

  const [
    _submitLesson,
    { loading: submittingLesson, error: submitLessonError, data: submitData }
  ] = useMutation<SubmitLessonData, SubmitLessonVars>(
    GraphQL.Mutation.SUBMIT_LESSON,
    {
      client: assessmentApolloClient
    }
  );

  const [
    _unsubmitLesson,
    {
      loading: unsubmittingLesson,
      error: unsubmitLessonError,
      data: unsubmitData
    }
  ] = useMutation<UnsubmitLessonData, UnsubmitLessonVars>(
    GraphQL.Mutation.UNSUBMIT_LESSON,
    {
      client: assessmentApolloClient
    }
  );

  function upsertLessonResult(
    lessonkey: number,
    lessonVersion: number,
    layer?: number,
    state?: number
  ) {
    return _upsertLessonResult({
      variables: {
        userkey,
        modulekey: lessonkey,
        moduleVersion: lessonVersion,
        layer,
        state
      }
    });
  }

  function deleteLessonResult(lessonkey: number, layer: number) {
    return _deleteLessonResult({
      variables: {
        userkey,
        modulekey: lessonkey,
        layer
      }
    });
  }

  function finishLessonSession(
    lessonType: LessonType,
    lessonkey: number,
    lessonVersion: number,
    layer: number,
    completed: boolean
  ) {
    return _finishLessonSession({
      variables: {
        lessonType,
        lessonkey,
        lessonVersion,
        layer,
        completed
      }
    });
  }

  function submitLesson(
    lessonkey: number,
    lessonVersion: number,
    layer: number,
    retry: boolean
  ) {
    return _submitLesson({
      variables: {
        modulekey: lessonkey,
        moduleVersion: lessonVersion,
        layer,
        retry
      }
    });
  }

  function unsubmitLesson(
    lessonkey: number,
    lessonVersion: number,
    layer: number
  ) {
    return _unsubmitLesson({
      variables: {
        modulekey: lessonkey,
        moduleVersion: lessonVersion,
        layer
      }
    });
  }

  return {
    savingLessonResult: loading,
    saveLessonResultError: !!error,
    savedLessonResult: resultData?.upsertLessonResult,
    upsertLessonResult,
    deleting,
    deleteError: !!_deleteError,
    deleted: deleteData?.deleteLessonResult,
    deleteLessonResult,
    finishLessonSession,
    finishingSession,
    finishSessionError,
    finishedAttempt: finishedSessionData?.finishLessonSession,
    submitLesson,
    submittingLesson,
    submitLessonError,
    submittedLesson: submitData?.submitLesson,
    unsubmitLesson,
    unsubmittingLesson,
    unsubmitLessonError,
    unsubmittedLesson: unsubmitData?.unsubmitLesson
  };
}
